
import { useEffect, useState } from "react";
import { FilledButton, OutlinedButton } from "../../common/Button";
import Lightbox from "../../common/Lightbox";
import TextInputField from "../../common/TextInputField";

import DatePicker from "react-datepicker";
import { ITask } from "../../../data_models/task";


import RichTextEditor from "../../common/RichTextEditor";
import useTasks from "../../../hooks/useTasks";
import useArielle from "../../../hooks/useArielle";
import AISuggestion from "../../common/AISuggestion";
import { IProject } from "../../../data_models/project";
import useProjects from "../../../hooks/useProjects";
import useDisplayNotifications from "../../../hooks/useDisplayNotifications";


interface EditTaskHeaderLightboxProps {
    task: ITask;
    projectId: string;
    onClose: () => void;
    onUpdateTask: (updatedTask: ITask) => void;
    onUpdateProject: (updatedProject: IProject) => void;
}

const EditTaskHeaderLightbox: React.FC<EditTaskHeaderLightboxProps> = ({ task, projectId, onClose, onUpdateTask, onUpdateProject }) => {

    const { updateTask } = useTasks();
    const { fetchProjectById } = useProjects();
    const { requestBriefTaskDescription, lastReceivedBriefTaskDescription, requestTaskTitle, lastReceivedTaskTitle } = useArielle();
    const { addSuccessNotification, addErrorNotification } = useDisplayNotifications();

    const [loading, setLoading] = useState(false);
    const [title, setTitle] = useState<string>();
    const [credits, setCredits] = useState<number>();
    const [briefDescription, setBriefDescription] = useState<string>();
    const [dueDate, setDueDate] = useState<Date | null>();
    const [startDate, setStartDate] = useState<Date | null>();

    const [arielleBriefTaskDescription, setArielleBriefTaskDescription] = useState<string>();
    const [arielleTaskTitle, setArielleTaskTitle] = useState<string>();

    const [arielleTaskTitleLoading, setArielleTaskTitleLoading] = useState(false);
    const [arielleBriefTaskDescriptionLoading, setArielleBriefTaskDescriptionLoading] = useState(false);


    useEffect(() => {
        if (task) {
            setTitle(task.title);
            setCredits(task.estimatedCredits);
            setBriefDescription(task.briefDescription);
            setDueDate(task.dueDate);
            setStartDate(task.startDate);

            requestBriefTaskDescription(task._id!);
            requestTaskTitle(task._id!);
        }
    }, [task]);

    useEffect(() => {
        if (lastReceivedBriefTaskDescription) {
            setArielleBriefTaskDescriptionLoading(false);
            setArielleBriefTaskDescription(lastReceivedBriefTaskDescription.briefDescription);
        }
    }, [lastReceivedBriefTaskDescription,]);

    useEffect(() => {
        if (lastReceivedTaskTitle) {
            setArielleTaskTitleLoading(false);
            setArielleTaskTitle(lastReceivedTaskTitle.title);
        }
    }, [lastReceivedTaskTitle]);


    const onUpdate = async () => {
        try {
            setLoading(true);

            const updateObj: ITask = {
                projectId: projectId,
                title: title,
                estimatedCredits: credits || 0,

                briefDescription: briefDescription,
                dueDate: dueDate,
                startDate: startDate,

            };
            const updatedTask = await updateTask(task._id!, updateObj);
            onUpdateTask(updatedTask);
            if (updateObj.estimatedCredits !== task.estimatedCredits) {
                const updatedProject = await fetchProjectById(projectId);
                onUpdateProject(updatedProject);
            }
            onClose();
            addSuccessNotification('Task updated successfully');
        } catch (error: any) {
            addErrorNotification('Failed to update task');
        } finally {
            setLoading(false);
        }
    };

    const onRefreshArielleTaskTitle = () => {
        setArielleTaskTitleLoading(true);
        const useTask: ITask = {
            ...task,
            title: title,
            briefDescription: briefDescription,
            estimatedCredits: credits,
            dueDate: dueDate,
            startDate: startDate,
        }
        requestTaskTitle(task._id!, useTask);
    }

    const onRefreshArielleBriefTaskDescription = () => {
        setArielleBriefTaskDescriptionLoading(true);
        const useTask: ITask = {
            ...task,
            title: title,
            briefDescription: briefDescription,
            estimatedCredits: credits,
            dueDate: dueDate,
            startDate: startDate,
        }
        requestBriefTaskDescription(task._id!, useTask);
    }

    return (

        <Lightbox
            header='Edit Task'
            onCancel={onClose}
            onConfirm={onUpdate}
            loadingConfirm={loading}
            confirmText='Update'
            onClose={onClose}
            content={
                <div className="flex flex-col items-start space-y-6" >
                    <div className="w-full">
                        <div className="flex items-center gap-2">
                            <label>Title</label>
                        </div>

                        <div className="w-full mb-2">
                            <TextInputField
                                value={title || ''}
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                }}
                                placeholder="Title" />
                        </div>
                        {arielleTaskTitle && <AISuggestion
                            onAccept={() => { setTitle(arielleTaskTitle) }}
                            onReject={() => { setArielleTaskTitle(undefined); }}
                            isRefreshLoading={arielleTaskTitleLoading}
                            onRefresh={onRefreshArielleTaskTitle}
                        >{arielleTaskTitle}
                        </AISuggestion>}
                    </div>

                    <div className="w-full">

                        <div className="w-full flex">
                            <div className="w-1/2">
                                <div className="flex items-center gap-2">
                                    <label>Start Date</label>
                                </div>
                                <div className="">
                                    <DatePicker
                                        selected={startDate}
                                        onChange={(date: Date | null) => setStartDate(date ? date : undefined)}
                                        placeholderText="Start Date"
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            <div className="w-1/2">
                                <div className="flex items-center gap-2">
                                    <label>Due Date</label>
                                </div>
                                <div className="">
                                    <DatePicker
                                        selected={dueDate}
                                        onChange={(date: Date | null) => setDueDate(date ? date : undefined)}
                                        placeholderText="Due Date"
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="w-full">
                        <div className="flex items-center gap-2">
                            <label>Brief</label>
                        </div>
                        <div className="w-full mb-2">
                            <RichTextEditor
                                value={briefDescription || ''}
                                onChange={(content) => { setBriefDescription(content); }}
                            />
                        </div>
                        {arielleBriefTaskDescription && <AISuggestion
                            onAccept={() => { setBriefDescription(arielleBriefTaskDescription); }}
                            onReject={() => { setArielleBriefTaskDescription(undefined); }}
                            isRefreshLoading={arielleBriefTaskDescriptionLoading}
                            onRefresh={onRefreshArielleBriefTaskDescription}
                        >{arielleBriefTaskDescription}
                        </AISuggestion>}
                    </div>

                    <div className="w-full">
                        <div className="flex items-center gap-2">
                            <label>Credits</label>
                        </div>
                        <div className="w-full">
                            <TextInputField
                                value={credits?.toString() || '0'}
                                onChange={(e) => {
                                    setCredits(parseFloat(e.target.value));
                                }}
                                type='number'
                                placeholder="20" />
                        </div>
                    </div>
                </div>
            }
        />

    );
}

export default EditTaskHeaderLightbox;
